import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Cube, TrendUp, TrendDown, MagnifyingGlass } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stock Ledger — per-product live balances plus a full audit trail of every
 * movement (opening, purchase, sale, manual). Balances are kept in sync by
 * the backend `inventory.py` engine; this page just renders the history.
 */
export default function StockLedger() {
  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState("");
  const [movements, setMovements] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    fetchLedger(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, month, fromDate, toDate]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/stock/summary`);
      setSummary(r.data || []);
      await fetchLedger("");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async (pid) => {
    try {
      const params = new URLSearchParams();
      if (pid) params.set("product_id", pid);
      if (month) params.set("month", month);
      else {
        if (fromDate) params.set("from_date", fromDate);
        if (toDate) params.set("to_date", toDate);
      }
      const r = await axios.get(`${API}/stock/ledger?${params}`);
      setMovements(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return summary.filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [summary, query]);

  const totals = useMemo(() => {
    const lowCount = summary.filter((s) => s.low && s.reorder_level > 0).length;
    const outCount = summary.filter((s) => s.current_stock <= 0).length;
    return { lowCount, outCount, total: summary.length };
  }, [summary]);

  const typeColor = {
    opening: "bg-slate-100 text-slate-700",
    purchase: "bg-emerald-100 text-emerald-700",
    sale: "bg-rose-100 text-rose-700",
    credit_note: "bg-emerald-50 text-emerald-600",
    debit_note: "bg-rose-50 text-rose-600",
    adjustment: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="p-4 sm:p-8" data-testid="stock-ledger-page">
      {/* Header */}
      <div
        className="rounded-sm p-6 sm:p-8 mb-6 text-white"
        style={{
          background:
            "linear-gradient(135deg,#064E3B 0%,#065F46 60%,#0E7490 100%)",
        }}
      >
        <div className="flex items-center gap-3 mb-2">
          <Cube size={36} weight="duotone" />
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Stock Ledger</h1>
        </div>
        <p className="text-emerald-50 text-sm">
          Live inventory balances + full movement history. Updates automatically on every
          invoice (sale ↓), purchase (purchase ↑) and product opening balance.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-sm text-sm">
            <b>{totals.total}</b> products tracked
          </span>
          <span className="px-3 py-1 bg-amber-400/20 border border-amber-300/40 rounded-sm text-sm">
            <b>{totals.lowCount}</b> below reorder
          </span>
          <span className="px-3 py-1 bg-red-400/20 border border-red-300/40 rounded-sm text-sm">
            <b>{totals.outCount}</b> out of stock
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT — product list */}
        <div className="lg:col-span-4 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-3 border-b border-slate-200 relative">
            <MagnifyingGlass size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="stock-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search product…"
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            onClick={() => setSelected("")}
            className={`w-full text-left px-4 py-3 text-sm border-b border-slate-100 ${
              !selected ? "bg-emerald-50 text-emerald-800 font-bold" : "hover:bg-slate-50"
            }`}
            data-testid="stock-show-all"
          >
            All movements
          </button>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-sm text-slate-400">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-400 text-center">No products yet</p>
            ) : (
              filtered.map((p) => {
                const isLow = p.current_stock <= p.reorder_level && p.reorder_level > 0;
                const isOut = p.current_stock <= 0;
                const cls = isOut
                  ? "text-red-700"
                  : isLow
                  ? "text-amber-700"
                  : "text-emerald-700";
                return (
                  <button
                    key={p.product_id}
                    onClick={() => setSelected(p.product_id)}
                    data-testid={`stock-product-${p.product_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 transition ${
                      selected === p.product_id ? "bg-emerald-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                    <p className={`text-xs font-bold ${cls}`}>
                      {p.current_stock} {p.unit}
                      {p.reorder_level > 0 && (
                        <span className="ml-2 text-slate-400 font-normal">
                          reorder at {p.reorder_level}
                        </span>
                      )}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — ledger movements */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-bold text-slate-700">
              {selected
                ? `Ledger: ${summary.find((s) => s.product_id === selected)?.name || ""}`
                : "All Stock Movements (latest first)"}
              {(month || fromDate || toDate) && (
                <span className="ml-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm" data-testid="stock-filter-badge">
                  {month ? `Month: ${month}` : `${fromDate || "…"} → ${toDate || "…"}`}
                </span>
              )}
            </h3>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500">Month</label>
                <input type="month" data-testid="stock-month-filter" value={month}
                  onChange={(e) => { setMonth(e.target.value); if (e.target.value) { setFromDate(""); setToDate(""); } }}
                  className="px-2 py-1 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500">From</label>
                <input type="date" data-testid="stock-from-filter" value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); if (e.target.value) setMonth(""); }}
                  className="px-2 py-1 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500">To</label>
                <input type="date" data-testid="stock-to-filter" value={toDate}
                  onChange={(e) => { setToDate(e.target.value); if (e.target.value) setMonth(""); }}
                  className="px-2 py-1 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              {(month || fromDate || toDate) && (
                <button type="button" data-testid="stock-filter-clear"
                  onClick={() => { setMonth(""); setFromDate(""); setToDate(""); }}
                  className="px-2 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-100">
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto sticky-table">
            <table className="w-full text-sm">
              <thead className="sticky-thead bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  {!selected && <th className="px-3 py-2 text-left">Product</th>}
                  <th className="px-3 py-2 text-right">In</th>
                  <th className="px-3 py-2 text-right">Out</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr>
                    <td colSpan={selected ? 6 : 7} className="px-3 py-6 text-center text-slate-400">
                      {(month || fromDate || toDate) ? "No movements in the selected period" : "No movements yet"}
                    </td>
                  </tr>
                )}
                {movements.map((m) => (
                  <tr
                    key={m.id}
                    data-testid={`movement-${m.id}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {format(new Date(m.ts), "dd MMM yyyy HH:mm")}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                          typeColor[m.movement_type] || "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {m.qty > 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
                        {m.movement_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">
                      {m.ref_doc_label || "—"}
                    </td>
                    {!selected && (
                      <td className="px-3 py-2 text-slate-700">{m.product_name}</td>
                    )}
                    <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                      {m.qty > 0 ? `+${m.qty}` : ""}
                    </td>
                    <td className="px-3 py-2 text-right text-rose-700 font-semibold">
                      {m.qty < 0 ? m.qty : ""}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {m.balance_after} {m.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
